import { createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { UNSAFE_DataRouterContext, useBlocker, type BlockerFunction, type Location } from 'react-router-dom';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';
import { RouteChangeGuard } from './RouteChangeGuard';

/** A navigation as `shouldBlock` sees it: where the page is, and where it would go. */
export interface LeaveNavigation {
  currentLocation: Location;
  nextLocation: Location;
}

export interface UnsavedChangesGuardOptions {
  /** Saves the changes; resolves to whether it worked (Save and continue leaves only then). */
  save: () => Promise<boolean>;
  /** Puts back the last saved values (Discard changes, before leaving). */
  discard: () => void;
  /** False while the changes cannot be saved as they stand: Save and continue is disabled. */
  canSave?: boolean;
  /** A save is running elsewhere (the footer's): the dialog waits on it. */
  saving?: boolean;
  /** The dialog's body. Defaults to the settings pages' sentence (UNSAVED_SECTION_MESSAGE). */
  message?: ReactNode;
  /**
   * A save a card runs by its own button is still running (useSaveHold): leaving waits for its
   * answer. Default false.
   */
  held?: boolean;
  /** Resolves once those saves settle, to whether they all worked (FooterSaveState.settleHeld). */
  settleHeld?: () => Promise<boolean>;
  /**
   * Which navigations ask first, in place of the default rule (a change of PATHNAME only). It is asked
   * only while there are changes (or a held save), as the default is. The Brand Workspace pages pass
   * `brandLeaveBlocks` (features/brand/leaveRules.ts), which also asks on a workspace switch
   * (`?ws=a` to `?ws=b`), a change of the search the default lets through. Pass it on every render, or
   * never: a page either has its own rule or uses the default.
   */
  shouldBlock?: (navigation: LeaveNavigation) => boolean;
}

/**
 * Asks before the page is left with changes that are not saved (spec "Saving and unsaved changes").
 * While `dirty`:
 * - a navigation inside the app to another PATHNAME (a sidebar link, the account menu, Back) is held and
 *   the Unsaved changes dialog asks first: Keep editing stays, Discard changes discards then goes, Save
 *   and continue saves then goes, or stays if the save fails. A change of the search alone is never held
 *   (`?ws=`, `?compose=`, `?generate=`, `?tab=`): see RouteChangeGuard. A page with its own rule
 *   (`shouldBlock`) is asked instead which navigations to hold.
 * - closing the tab, reloading or leaving the app gets the browser's own "Leave site?" prompt
 *   (beforeunload): no page may draw its own dialog there.
 *
 * While `held` (a save a card runs by its own button has not answered yet), the same navigation waits
 * for that answer without a dialog, then goes if the save worked and stays if it failed, where the card
 * shows why; the browser's prompt is armed meanwhile.
 *
 * Returns the dialog: render it anywhere in the page (`{guard}`). The router half needs a data router
 * (createBrowserRouter in the app, createMemoryRouter in a spec); under a plain MemoryRouter it stands
 * down and only the browser prompt is armed, so a page spec that renders under one still works.
 */
export function useUnsavedChangesGuard(dirty: boolean, options: UnsavedChangesGuardOptions): ReactNode {
  const leaving = dirty || options.held === true;
  useEffect(() => {
    if (!leaving) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome and Edge before 119 ask only when this is set.
      event.returnValue = true;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [leaving]);

  const dataRouter = useContext(UNSAFE_DataRouterContext);
  if (!dataRouter) return null;
  const { shouldBlock, ...rest } = options;
  return shouldBlock
    ? createElement(RuleRouteGuard, { dirty, shouldBlock, ...rest })
    : createElement(RouteChangeGuard, { dirty, ...rest });
}

const settledAlready = () => Promise.resolve(true);

type RuleRouteGuardProps = Omit<UnsavedChangesGuardOptions, 'shouldBlock'> & {
  dirty: boolean;
  shouldBlock: (navigation: LeaveNavigation) => boolean;
};

/**
 * The route half for a page with its own rule (`shouldBlock`): RouteChangeGuard's flow, step for step
 * (the hold on a running save, the dialog's three answers, the focus back on the link that asked), with
 * the page's rule deciding which navigations are held. The pages without a rule keep RouteChangeGuard
 * itself, untouched; this copy exists because RouteChangeGuard fixes its rule to the pathname. Keep the
 * two in step: a fix to one belongs in both.
 */
function RuleRouteGuard({
  dirty,
  shouldBlock,
  save,
  discard,
  canSave = true,
  saving = false,
  message,
  held = false,
  settleHeld = settledAlready,
}: RuleRouteGuardProps) {
  const blockerFunction = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => (dirty || held) && shouldBlock({ currentLocation, nextLocation }),
    [dirty, held, shouldBlock],
  );
  const blocker = useBlocker(blockerFunction);
  const [busy, setBusy] = useState(false);
  // The blocker and `dirty` as they are NOW, for the answer to a save that comes after the render that
  // waited on it.
  const latest = useRef({ blocker, dirty });
  useIsoLayoutEffect(() => {
    latest.current = { blocker, dirty };
  });

  // A save is still running (or the hold that blocked has just ended): no dialog yet; wait for its
  // answer. A failure stays; a save that worked goes on, or, with changes unsaved, lets the dialog ask.
  // Never while the dialog's own Save and continue runs (`busy`), which goes on by itself once it answers.
  const waitingOnSave = blocker.state === 'blocked' && !busy && (held || !dirty);
  useEffect(() => {
    if (!waitingOnSave) return;
    let current = true;
    void settleHeld().then((ok) => {
      const { blocker: now, dirty: dirtyNow } = latest.current;
      if (!current || now.state !== 'blocked') return;
      if (!ok) now.reset();
      else if (!dirtyNow) now.proceed();
    });
    return () => {
      current = false;
    };
  }, [waitingOnSave, settleHeld]);

  // Where the focus was when the navigation was held, so Keep editing can put it back. Read while
  // rendering: the dialog moves the focus into itself before any effect of this component runs.
  const opener = useRef<HTMLElement | null>(null);
  if (blocker.state !== 'blocked' || waitingOnSave) {
    opener.current = null;
    return null;
  }
  if (opener.current === null && typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
    opener.current = document.activeElement;
  }

  return createElement(UnsavedChangesDialog, {
    open: true,
    message,
    saving: busy || saving,
    canSave,
    returnFocusTo: opener.current,
    onKeepEditing: () => blocker.reset(),
    onDiscard: () => {
      discard();
      blocker.proceed();
    },
    onSave: async () => {
      setBusy(true);
      let ok = false;
      try {
        ok = await save();
      } catch {
        ok = false;
      }
      setBusy(false);
      // A failed save stays on the page, where the card shows why.
      if (ok) blocker.proceed();
      else blocker.reset();
    },
  });
}
